import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";

import DataTable, {
  createColumnHelper,
  createFilter,
  sorting,
} from "veneer/DataTable";

import { deviceUptimeReportSelector } from "cortex/ui/src/fleet/redux-store/ui-state/report-uptime-device/selectors";
import { Device } from "cortex/ui/src/fleet/view/device/model";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { Network } from "cortex/ui/src/network/model";
import {
  deviceStoresLoadedSelector,
  devicesSelector,
} from "cortex/ui/src/redux-store/devices";
import {
  networksSelector,
  networksStoresLoadedSelector,
} from "cortex/ui/src/redux-store/networks";
import { NavLoader } from "cortex/ui/src/ui/loader";
import Notification from "cortex/ui/src/ui/notification";
import {
  formatDateTimeLocal,
  formatPercentage,
} from "cortex/ui/src/ui/table/formats";
import { getErrorMessage } from "cortex/ui/src/util/response";
import { Container, Panel } from "cortex/ui/src/view/layout";

import ReportFilter from "./ReportFilter";
import UptimeChart from "./UptimeChart";
import {
  type FetchUptimeDeviceReportArgs,
  fetchUptimeDeviceReport,
  unloadUptimeDeviceReport,
} from "../action";
import { UptimeDeviceRow } from "../model";

export default function UptimeDeviceReport() {
  const { deviceId, networkId, start, end } = useParams<{
    deviceId?: string;
    networkId?: string;
    start?: string;
    end?: string;
  }>();
  const dispatch = useAppDispatch();
  const devices = useAppSelector(devicesSelector);
  const networks = useAppSelector(networksSelector);
  const networksLoaded = useAppSelector(networksStoresLoadedSelector);
  const devicesLoaded = useAppSelector(deviceStoresLoadedSelector);
  const storesLoaded = networksLoaded && devicesLoaded;
  const report = useAppSelector(deviceUptimeReportSelector);
  const device: Device | undefined = useMemo(
    () => (deviceId ? devices?.[deviceId] : undefined),
    [devices, deviceId],
  );
  const network: Network | undefined = useMemo(
    () => (networkId ? networks?.[networkId] : undefined),
    [networks, networkId],
  );

  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (!device || !start || !end) {
      unloadUptimeDeviceReport(dispatch);
    }
  }, [device, dispatch, end, start]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      unloadUptimeDeviceReport(dispatch);
    };
  }, [dispatch]);

  useEffect(() => {
    const canFetch = Boolean(device && end && start);
    if (!canFetch) return;

    const args: FetchUptimeDeviceReportArgs = {
      deviceId: device!.id,
      end: end!,
      start: start!,
    };

    setSubmitted(true);

    fetchUptimeDeviceReport(dispatch, args)
      .catch((error) => {
        const msg = getErrorMessage(
          error,
          "Failed to fetch device uptime report.",
        );
        setErrorMessage(msg);
      })
      .finally(() => setSubmitted(false));
  }, [device, dispatch, end, start]);

  const exportFileName = useCallback(
    (prefix: string) => {
      const deviceId = device ? device.device_id : "";
      return `${prefix}_${deviceId}_${start}_${end}.csv`;
    },
    [device, end, start],
  );

  const downloadCsv = useCallback(
    (rows: ReadonlyArray<UptimeDeviceRow>) => {
      const header = [["Date", "Uptime", "In Network"]];
      const lines = rows.map((row) => {
        const dateStr = moment.isMoment(row.date)
          ? row.date.format("YYYY-MM-DD")
          : moment(row.date).format("YYYY-MM-DD");
        const uptime = Number.isFinite(row.uptime) ? row.uptime : 0;
        const inNetwork = row.inNetwork ? "Yes" : "No";
        return [dateStr, uptime, inNetwork];
      });

      const escaped = [header, lines]
        .flat()
        .map((arr) =>
          arr.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");

      const blob = new Blob([escaped], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFileName("device-uptime");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    [exportFileName],
  );

  const renderNetworkUptimeReport = useCallback(() => {
    if (!report || Object.keys(report).length === 0) {
      return <p>No data to display.</p>;
    }

    const data: ReadonlyArray<UptimeDeviceRow> = Object.keys(report).map(
      (d) => ({
        date: moment(d),
        inNetwork: Boolean(network),
        uptime: report[d],
      }),
    );

    const filter = createFilter<UptimeDeviceRow>("date");
    const columnHelper = createColumnHelper<UptimeDeviceRow>();

    const columns = [
      columnHelper.accessor("date", {
        header: "Date",
        cell: (prop) => {
          const date = prop.getValue();
          if (!network) {
            return formatDateTimeLocal(date, "MM/DD/YYYY");
          }

          const url =
            `/diagnostics/uptime-report/${network.id}/` +
            `${start}/${end}/devices/` +
            `${date.format("YYYY-MM-DD")}`;

          return (
            <Link to={url}>{formatDateTimeLocal(date, "MM/DD/YYYY")}</Link>
          );
        },
        sortingFn: sorting.datetime((row) => row.date),
      }),
      columnHelper.accessor("uptime", {
        header: "Uptime",
        cell: (prop) => formatPercentage(prop.getValue()),
        sortingFn: sorting.number((row) => row.uptime),
      }),
      columnHelper.accessor("inNetwork", {
        header: "In Network",
        cell: (prop) => (prop.getValue() ? "Yes" : "No"),
        sortingFn: sorting.boolean((row) => row.inNetwork),
      }),
    ];

    return (
      <DataTable
        columns={columns}
        filter={filter}
        initialPageSize={10}
        initialSort={{ id: "date", desc: true }}
        onExport={(rows) => downloadCsv(rows)}
        rows={data}
      />
    );
  }, [downloadCsv, network, report]);

  const title = device ? (
    <>
      Device Uptime Report:{" "}
      <Link to={`/devices/${device.device_id}/overview`}>
        {device.name} {`(${device.device_id})`}
      </Link>
    </>
  ) : (
    "Device Uptime Report"
  );

  if (!storesLoaded) {
    return (
      <Container title="Uptime Report">
        <NavLoader.Proxy />
      </Container>
    );
  }

  return (
    <Container hideBox title="Uptime Report">
      {errorMessage && <Notification error={errorMessage} />}
      <ReportFilter
        end={end}
        network={network}
        start={start}
        submitted={submitted}
      />
      <Panel borderRadius={2} title={title}>
        {network && Object.keys(report || {}).length > 0 ? (
          <UptimeChart data={report} />
        ) : null}
        {renderNetworkUptimeReport()}
      </Panel>
    </Container>
  );
}
